import { synchronizationMessage, synchronizationLabel, isTerminalCommitment } from '@/hooks/useCommitments'
import React from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ApiError } from '@/lib/apiClient'
import { Button } from '@/components/common/Button'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionTitle } from '@/components/common/SectionTitle'
import { CommitmentForm } from '@/components/commitments/CommitmentForm'
import {
  useRelationshipDetail,
  type CreatePersonaInput,
  type PersonaRolEnRelacion,
  type RelationshipEstado,
  type RelationshipPriority,
  type UpdateRelationshipInput,
} from '@/hooks/useRelationships'
import { usePersonas, type PersonaListItem } from '@/hooks/usePersonas'
import { useCommitments, type CreateCommitmentInput, type UpdateCommitmentInput } from '@/hooks/useCommitments'
import { useAssignableUsuarios } from '@/hooks/useUsuarios'
import { findEmailConflict, findPersonaMatches } from '@/lib/personaMatching'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useRelationshipEvidence, type EvidenceSourceState } from '@/hooks/useRelationshipEvidence'
import { useCommercialSignals } from '@/hooks/useCommercialSignals'
import { CommercialSignalsPanel } from '@/components/commercial/CommercialSignalsPanel'
import './prototype.css'

const estadoLabel: Record<RelationshipEstado, string> = {
  activa: 'Activa',
  requiere_atencion: 'Se enfría',
  pausada: 'Pausada',
  cerrada: 'Cerrada',
}
const ESTADOS: RelationshipEstado[] = ['activa', 'requiere_atencion', 'pausada', 'cerrada']
const PRIORIDADES: RelationshipPriority[] = ['p1', 'p2', 'p3']
const prioridadLabel: Record<RelationshipPriority, string> = {
  p1: 'P1 · Prioridad alta',
  p2: 'P2 · Prioridad media',
  p3: 'P3 · Prioridad baja',
}

const rolEnRelacionLabel: Record<PersonaRolEnRelacion, string> = {
  decisor: 'Decisor',
  influenciador: 'Influenciador',
  operativo: 'Operativo',
  sponsor: 'Sponsor',
  otro: 'Otro',
}
const ROLES_EN_RELACION: PersonaRolEnRelacion[] = ['decisor', 'influenciador', 'operativo', 'sponsor', 'otro']

const emptyPersonaForm: CreatePersonaInput = { title: '', cargo: '', email: '', telefono: '', rolEnRelacion: null }

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })
}

function sourceStateLabel(state: EvidenceSourceState): string {
  return ({
    ok: 'Disponible',
    not_owner: 'Otro responsable',
    consent_required: 'Consentimiento pendiente',
    not_configured: 'No configurada',
    timeout: 'Sin respuesta',
    degraded: 'Degradada',
  })[state]
}

const Field: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
  <div className="prototypeRelationshipRow__cell">
    <span className="prototypeMiniLabel">{label}</span>
    <div>{value ?? '—'}</div>
  </div>
)

export const RelacionDetalle: React.FC = () => {
  const { relationshipId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { status, detail, error, reload, update, remove, createPersona } = useRelationshipDetail(relationshipId)
  const { createCommitment, updateCommitment, synchronizeCommitment } = useCommitments()
  // Personas es de bajo volumen (MASTER.md Q.2: "2–5× organizaciones") — se
  // carga completa y se filtra client-side, igual que Relaciones/Personas
  // ya hacen en sus propias listas. No existe (ni se agrega aquí) un
  // endpoint de búsqueda server-side.
  const { personas: allPersonas } = usePersonas()
  const assignableUsers = useAssignableUsuarios()
  const currentUser = useCurrentUser()
  const evidence = useRelationshipEvidence(relationshipId)
  const signals = useCommercialSignals({ organizationId: relationshipId })

  const [editing, setEditing] = React.useState(false)
  const [estado, setEstado] = React.useState<RelationshipEstado>('activa')
  const [prioridad, setPrioridad] = React.useState<RelationshipPriority | ''>('')
  const [resumen, setResumen] = React.useState('')
  const [responsableId, setResponsableId] = React.useState('')
  const [activo, setActivo] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [deleting, setDeleting] = React.useState(false)
  const [deleteError, setDeleteError] = React.useState<string | null>(null)

  const [addingPersona, setAddingPersona] = React.useState(false)
  const [personaForm, setPersonaForm] = React.useState<CreatePersonaInput>(emptyPersonaForm)
  const [personaSearch, setPersonaSearch] = React.useState('')
  const [personaSaving, setPersonaSaving] = React.useState(false)
  const [personaError, setPersonaError] = React.useState<string | null>(null)
  const personaFormRef = React.useRef<HTMLDivElement>(null)
  const commitmentSectionRef = React.useRef<HTMLElement>(null)
  const [addingCommitment, setAddingCommitment] = React.useState(false)
  const [commitmentNotice, setCommitmentNotice] = React.useState<string | null>(null)
  const [reschedulingId, setReschedulingId] = React.useState<string | null>(null)
  const [rescheduleDate, setRescheduleDate] = React.useState('')
  const [updatingCommitmentId, setUpdatingCommitmentId] = React.useState<string | null>(null)
  const [evidenceNotice, setEvidenceNotice] = React.useState<string | null>(null)

  const searchResults = React.useMemo(
    () => findPersonaMatches(allPersonas, personaSearch),
    [allPersonas, personaSearch]
  )

  // Modelo V1 (MASTER.md Q.2): Personas.Organizacion es un lookup requerido
  // de valor único — una persona no puede tener dos organizaciones en la
  // misma fila. Por eso el email debe ser único globalmente (índice usado
  // para matching de participantes Graph): si ya existe, no se "vincula" la
  // fila existente a esta relación (el modelo no lo permite), se avisa para
  // que el usuario decida crear una fila nueva con otro correo, o vaya a
  // ver la ficha ya existente.
  const emailConflict = React.useMemo(
    () => findEmailConflict(allPersonas, personaForm.email),
    [allPersonas, personaForm.email]
  )

  const applyExistingPersona = (p: PersonaListItem) => {
    setPersonaForm({
      title: p.title,
      cargo: p.cargo ?? '',
      email: '',
      telefono: p.telefono ?? '',
      rolEnRelacion: p.rolEnRelacion,
    })
    setPersonaSearch('')
    setPersonaError(null)
  }

  React.useEffect(() => {
    if (detail) {
      setEstado(detail.organizacion.estadoRelacion)
      setPrioridad(detail.organizacion.prioridadComercial ?? '')
      setResumen(detail.organizacion.resumenActual ?? '')
      setResponsableId(detail.organizacion.responsableId ?? '')
      setActivo(detail.organizacion.activo)
    }
  }, [detail])

  React.useEffect(() => {
    if (addingPersona) {
      personaFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [addingPersona])

  React.useEffect(() => {
    if (detail && location.hash === '#nueva-accion') {
      setAddingCommitment(true)
      window.requestAnimationFrame(() => commitmentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
    }
  }, [detail, location.hash])

  const closePersonaForm = () => {
    setAddingPersona(false)
    setPersonaForm(emptyPersonaForm)
    setPersonaSearch('')
    setPersonaError(null)
  }

  const openPersonaForm = () => {
    setPersonaForm(emptyPersonaForm)
    setPersonaSearch('')
    setPersonaError(null)
    setAddingPersona(true)
  }

  const openCommitmentForm = () => {
    setAddingCommitment(true)
    setCommitmentNotice(null)
    window.requestAnimationFrame(() => commitmentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }

  const save = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      const patch: UpdateRelationshipInput = {
        estadoRelacion: estado,
        prioridadComercial: prioridad || null,
        resumenActual: resumen.trim() || null,
        activo,
        ...(responsableId ? { responsableId } : {}),
      }
      await update(patch)
      setEditing(false)
    } catch {
      setSaveError('No se pudo guardar. Reintenta en unos segundos.')
    } finally {
      setSaving(false)
    }
  }

  const deleteRelationship = async () => {
    if (!detail || !window.confirm(`¿Eliminar definitivamente ${detail.organizacion.title}? Solo se puede eliminar una empresa sin personas, compromisos ni historia.`)) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await remove()
      navigate('/relaciones', { replace: true })
    } catch (error) {
      const message = error instanceof ApiError && error.code === 'API_REQUEST_FAILED'
        ? 'Esta empresa tiene contexto comercial. Desactívala en lugar de eliminarla.'
        : 'No se pudo eliminar la empresa.'
      setDeleteError(message)
    } finally {
      setDeleting(false)
    }
  }

  const updateCommitmentAction = async (id: string, patch: UpdateCommitmentInput, notice: string) => {
    setUpdatingCommitmentId(id)
    setCommitmentNotice(null)
    try {
      const result = await updateCommitment(id, patch)
      setReschedulingId(null)
      setRescheduleDate('')
      setCommitmentNotice(`${notice} ${synchronizationMessage(result.synchronization)}`)
      reload()
    } catch {
      setCommitmentNotice('No se confirmó la actualización del compromiso. Comprueba su estado antes de repetirla.')
    } finally {
      setUpdatingCommitmentId(null)
    }
  }

  const syncCommitment = async (id: string, adoptLegacy = false) => {
    setUpdatingCommitmentId(id)
    try { const result = await synchronizeCommitment(id,adoptLegacy); setCommitmentNotice(synchronizationMessage(result.synchronization)); reload() }
    catch { setCommitmentNotice('No se confirmó la sincronización. Revisa propietario, configuración o conflictos antes de reintentar.') }
    finally { setUpdatingCommitmentId(null) }
  }

  const savePersona = async () => {
    const title = personaForm.title.trim()
    if (!title) {
      setPersonaError('El nombre es obligatorio.')
      return
    }
    if (emailConflict) {
      setPersonaError('Ese correo ya pertenece a otra persona registrada. Usa la búsqueda de arriba o cambia el correo.')
      return
    }
    setPersonaSaving(true)
    setPersonaError(null)
    try {
      await createPersona({
        title,
        cargo: personaForm.cargo?.trim() || null,
        email: personaForm.email?.trim() || null,
        telefono: personaForm.telefono?.trim() || null,
        rolEnRelacion: personaForm.rolEnRelacion || null,
      })
      closePersonaForm()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setPersonaError('Ya existe una persona registrada con ese correo. Búscala arriba para ver su ficha o reutilizar sus datos con otro correo.')
      } else {
        setPersonaError('No se pudo guardar. Reintenta en unos segundos.')
      }
    } finally {
      setPersonaSaving(false)
    }
  }

  const stateMessage = (() => {
    switch (status) {
      case 'loading': return 'Cargando ficha…'
      case 'not_signed_in': return 'Inicia sesión con tu cuenta Microsoft para ver esta relación.'
      case 'not_configured': return 'La conexión con SharePoint aún no está habilitada en este entorno.'
      case 'forbidden': return 'Tu perfil aún no está habilitado en Wacner Sales.'
      default: return null
    }
  })()

  if (stateMessage) {
    return (
      <div className="prototypePage">
        <PageHeader title="Relación" subtitle="Ficha de organización" />
        <p className="prototypeRelationshipRow__summary">{stateMessage}</p>
        <Link to="/relaciones">← Volver a relaciones</Link>
      </div>
    )
  }

  if (status === 'unauthorized' || status === 'error' || !detail) {
    return (
      <div className="prototypePage">
        <PageHeader title="Relación" subtitle="Ficha de organización" />
        <p className="prototypeRelationshipRow__summary">
          No se pudo cargar la relación{error instanceof Error ? `: ${error.message}` : '.'}
        </p>
        <div className="prototypeContextCard__actions">
          <Button size="sm" variant="secondary" onClick={reload}>Reintentar</Button>
          <Link to="/relaciones"><Button size="sm" variant="ghost">Volver</Button></Link>
        </div>
      </div>
    )
  }

  const o = detail.organizacion
  const displayedHealth = evidence.data?.summary.health ?? detail.health
  const visibleInteractionDoesNotCount = Boolean(
    detail.ultimaInteraccion &&
      (!displayedHealth.lastActivity || Date.parse(detail.ultimaInteraccion.fecha) > Date.parse(displayedHealth.lastActivity)),
  )
  const healthSummary = displayedHealth.sourcesComplete
    ? displayedHealth.issues.length
      ? displayedHealth.issues.map((issue) => issue.label).join(' · ')
      : 'Al día según actividad y próxima acción verificadas.'
    : 'No verificable porque faltan fuentes.'

  const saveCommitment = async (input: CreateCommitmentInput) => {
    const result = await createCommitment(input)
    setAddingCommitment(false)
    setCommitmentNotice(synchronizationMessage(result.synchronization))
    reload()
  }

  const decideEvidence = async (candidateId: string, decision: 'confirm' | 'discard') => {
    setEvidenceNotice(null)
    try {
      await evidence.decide(candidateId, decision)
      setEvidenceNotice(decision === 'confirm' ? 'Asociación confirmada y verificada.' : 'Evidencia descartada para esta relación.')
    } catch {
      setEvidenceNotice('No se pudo verificar el resultado. La evidencia no se presenta como confirmada; revisa antes de reintentar.')
    }
  }

  return (
    <div className="prototypePage" data-evidence-status={evidence.status}>
      <PageHeader
        title={o.title}
        subtitle={o.nombreComercial ? `${o.nombreComercial} · ${estadoLabel[o.estadoRelacion]}` : estadoLabel[o.estadoRelacion]}
        action={
          <div className="prototypeContextCard__actions">
            <Button size="sm" onClick={() => setEditing((v) => !v)}>{editing ? 'Cerrar edición' : 'Editar'}</Button>
            {currentUser.data?.user.rol === 'administrador' && (
              <details className="commercialActionMenu">
                <summary>Más acciones</summary>
                <div className="commercialActionMenu__panel">
                  <Button size="sm" variant="danger" onClick={() => void deleteRelationship()} disabled={deleting}>{deleting ? 'Eliminando…' : 'Eliminar empresa'}</Button>
                </div>
              </details>
            )}
            <Link to="/relaciones"><Button size="sm" variant="secondary">Volver</Button></Link>
          </div>
        }
      />

      {deleteError && <p className="prototypeRelationshipRow__summary" role="alert">{deleteError}</p>}

      <section className="commercialCommandBar" aria-labelledby="commercial-health-title">
        <div className="commercialCommandBar__health">
          <span className="prototypeMiniLabel">Salud comercial · {displayedHealth.rulesVersion}</span>
          <h2 id="commercial-health-title">{healthSummary}</h2>
          {displayedHealth.lastActivity && <p>Última actividad válida: {formatDate(displayedHealth.lastActivity)}</p>}
          {visibleInteractionDoesNotCount && detail.ultimaInteraccion && (
            <details className="commercialEvidenceExplanation">
              <summary>La historia muestra una referencia más reciente. ¿Por qué no cuenta?</summary>
              <p>
                {['contacto', 'documento'].includes(detail.ultimaInteraccion.tipo)
                  ? `Un ${detail.ultimaInteraccion.tipo} aporta contexto, pero no acredita una interacción comercial.`
                  : 'La referencia visible no cumple todos los criterios de actividad válida.'}{' '}
                agenda-v2 sólo considera notas activas y vínculos confirmados de correo o reunión, con fecha válida y no futura.
              </p>
            </details>
          )}
        </div>
        <div className="commercialCommandBar__next">
          <span className="prototypeMiniLabel">Próxima acción</span>
          <strong>{detail.nextAction?.title ?? 'Sin próxima acción registrada'}</strong>
          {detail.nextAction?.vencimiento && <span>Vence {formatDate(detail.nextAction.vencimiento)}</span>}
          <Button size="sm" onClick={openCommitmentForm}>{detail.nextAction ? 'Agregar otra acción' : 'Registrar próxima acción'}</Button>
        </div>
      </section>

      {evidence.status === 'loading' && (
        <section className="prototypeSection commercialEvidenceLoading" aria-label="Cargando evidencia Microsoft 365">
          <span>Conectando evidencia comercial…</span><span /><span />
        </section>
      )}

      {evidence.status === 'error' && (
        <section className="prototypeSection commercialEvidenceState" role="status">
          <strong>La ficha comercial está disponible, pero la evidencia Microsoft 365 no pudo verificarse.</strong>
          <span>Ninguna ausencia se interpreta como falta de actividad.</span>
          <Button size="sm" variant="secondary" onClick={evidence.reload}>Reintentar evidencia</Button>
        </section>
      )}

      {evidence.data && (
        <>
          <section className="prototypeSection commercialEvidenceExecutive" aria-labelledby="evidence-summary-title">
            <SectionTitle
              title="Resumen ejecutivo verificable"
              subtitle={`Evidencia ${evidence.data.rulesVersion}. Las asociaciones ambiguas permanecen fuera de los hechos.`}
            />
            <div className="commercialEvidenceSummaryGrid">
              <Field label="Última interacción válida" value={evidence.data.summary.lastValidInteraction ? `${evidence.data.summary.lastValidInteraction.label} · ${formatDate(evidence.data.summary.lastValidInteraction.date)}` : 'Sin evidencia confirmada'} />
              <Field label="Próxima reunión" value={evidence.data.summary.nextMeeting ? `${evidence.data.summary.nextMeeting.title} · ${formatDate(evidence.data.summary.nextMeeting.date)}` : 'Sin reunión confirmada'} />
              <Field label="Próximo compromiso" value={evidence.data.summary.nextCommitment ? `${evidence.data.summary.nextCommitment.title} · ${formatDate(evidence.data.summary.nextCommitment.date)}` : 'Sin próxima acción'} />
              <Field label="Personas clave" value={evidence.data.summary.keyPeople.length ? evidence.data.summary.keyPeople.map((person) => person.title).join(', ') : 'Sin personas clave acreditadas'} />
              <Field label="Bloqueos" value={evidence.data.summary.blockers.length ? evidence.data.summary.blockers.join(' · ') : 'Sin bloqueos detectados'} />
              <Field label="Completitud comercial" value={`${evidence.data.summary.evidenceQuality.completeness}% · ${evidence.data.summary.evidenceQuality.verifiedTimelineItems} hechos verificados`} />
            </div>
            <div className="commercialEvidencePrimaryAction">
              <div><span className="prototypeMiniLabel">Siguiente acción</span><strong>{evidence.data.summary.nextAction}</strong></div>
              <Button size="sm" onClick={openCommitmentForm}>{evidence.data.summary.nextCommitment ? 'Agregar acción' : 'Registrar acción'}</Button>
            </div>
            <p className={`commercialEvidenceOwnership commercialEvidenceOwnership--${evidence.data.ownership.state}`}>{evidence.data.ownership.message}</p>
          </section>

          <CommercialSignalsPanel
            compact
            title="Señales activas"
            signals={signals.status === 'ready' ? signals.signals : evidence.data.commercialSignals}
            onTransition={signals.transition}
          />

          <section className="prototypeSection commercialEvidenceSources" aria-labelledby="source-health-title">
            <SectionTitle title="Cobertura Microsoft 365" subtitle="Cada fuente se consulta de forma independiente y solo con la sesión del responsable." />
            <div className="commercialEvidenceSourceGrid" id="source-health-title">
              {Object.entries(evidence.data.sourceStates).map(([source, state]) => (
                <div key={source} className={`commercialEvidenceSource commercialEvidenceSource--${state}`}>
                  <strong>{({ sales: 'Sales', mail: 'Mail', calendar: 'Calendar', contacts: 'Contacts', todo: 'To Do', documents: 'Documentos', teams: 'Teams', transcripts: 'Transcripciones' })[source] ?? source}</strong>
                  <span>{sourceStateLabel(state)}</span>
                </div>
              ))}
            </div>
          </section>

          {evidence.data.legacyUnverified.length > 0 && (
            <section className="prototypeSection commercialEvidenceLegacy" aria-labelledby="evidence-legacy-title">
              <SectionTitle title="Evidencia legacy sin ownership acreditado" subtitle="Se conserva visible para auditoría, pero no cuenta como historia confirmada, salud ni contexto factual de Copilot." />
              <div className="commercialEvidenceTimelineList" id="evidence-legacy-title">
                {evidence.data.legacyUnverified.map((item) => (
                  <article key={item.id} className="commercialEvidenceTimelineItem commercialEvidenceTimelineItem--legacy">
                    <span className={`commercialEvidenceIcon commercialEvidenceIcon--${item.type}`} aria-hidden="true" />
                    <div>
                      <div className="commercialEvidenceItemTop"><strong>{item.title}</strong><span>{formatDate(item.date)}</span></div>
                      <p><span className="commercialEvidenceSourceTag">{item.source}</span>{item.personName ? ` · Con ${item.personName}` : ''}</p>
                      <p className="commercialEvidenceWarning">Ownership legacy pendiente de acreditar. No es un hecho comercial confirmado.</p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          <div className="commercialEvidenceWorkspace">
            <section className="prototypeSection commercialEvidenceTimeline" aria-labelledby="commercial-timeline-title">
              <SectionTitle title="Historia comercial verificada" subtitle="Notas Sales, compromisos y vínculos Microsoft 365 confirmados, en una sola línea de tiempo." />
              {evidence.data.timeline.length === 0 ? (
                <div className="commercialEvidenceEmpty"><strong>Aún no hay historia verificable.</strong><span>Confirma evidencia sugerida o registra contexto cuando exista.</span></div>
              ) : (
                <div className="commercialEvidenceTimelineList" id="commercial-timeline-title">
                  {evidence.data.timeline.map((item) => (
                    <article key={item.id} className="commercialEvidenceTimelineItem">
                      <span className={`commercialEvidenceIcon commercialEvidenceIcon--${item.type}`} aria-hidden="true" />
                      <div>
                        <div className="commercialEvidenceItemTop"><strong>{item.title}</strong><span>{formatDate(item.date)}</span></div>
                        <p><span className="commercialEvidenceSourceTag">{item.source}</span>{item.timing === 'upcoming' ? ' · Próxima reunión' : item.timing === 'past' ? ' · Interacción pasada' : ''}{item.personName ? ` · Con ${item.personName}` : ''}{item.status ? ` · ${item.status}` : ''}</p>
                        {item.detail && <p>{item.detail}</p>}
                        {(item.direction || item.conversation) && <p>{item.direction ? `Correo ${item.direction}` : 'Correo'}{item.conversation ? ' · conversación agrupada en Outlook' : ''}{item.participants?.length ? ` · ${item.participants.join(', ')}` : ''}</p>}
                        {item.organizer && <p>Organiza {item.organizer}{item.attendees?.length ? ` · ${item.attendees.length} asistentes` : ''}{item.responseStatus ? ` · ${item.responseStatus}` : ''}</p>}
                        {item.location && <p>{item.location}{item.fileType ? ` · ${item.fileType}` : ''}</p>}
                      </div>
                      <div className="commercialEvidenceTimelineActions">
                        {item.type === 'compromiso' && (
                          <>
                            {isTerminalCommitment(item.status ?? '') && <Button size="sm" variant="ghost" disabled={updatingCommitmentId !== null} onClick={() => { if (window.confirm('¿Reabrir expresamente este compromiso terminado?')) void updateCommitmentAction(item.id.replace('compromiso:', ''), { estado: 'pendiente', reopen: true }, 'Compromiso reabierto.') }}>Reabrir compromiso</Button>}
                            <Button size="sm" variant="secondary" disabled={updatingCommitmentId !== null} onClick={() => void syncCommitment(item.id.replace('compromiso:', ''))}>Sincronizar / comprobar To Do</Button>
                            {item.synchronization?.status === 'adoption_required' && <Button size="sm" variant="ghost" onClick={() => { if (window.confirm('¿Adoptar la tarea anterior de este compromiso en tu propio To Do?')) void syncCommitment(item.id.replace('compromiso:', ''), true) }}>Adoptar tarea anterior</Button>}
                          </>
                        )}
                        {item.url && <a className="prototypeMicrosoftLink" href={item.url} target="_blank" rel="noreferrer">Abrir fuente</a>}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className="prototypeSection commercialEvidenceCandidates" aria-labelledby="evidence-candidates-title">
              <SectionTitle title="Evidencia por revisar" subtitle="Estas referencias no cuentan como hechos hasta que las confirmes." />
              {evidenceNotice && <p className="commercialEvidenceNotice" role="status">{evidenceNotice}</p>}
              {evidence.data.ownership.state === 'not_owner' ? (
                <div className="commercialEvidenceEmpty"><strong>Revisión restringida al responsable.</strong><span>La sesión actual no consulta ni vincula recursos personales de otra persona.</span></div>
              ) : evidence.data.candidates.length === 0 ? (
                <div className="commercialEvidenceEmpty"><strong>No hay asociaciones pendientes.</strong><span>Las fuentes disponibles no devolvieron coincidencias conservadoras.</span></div>
              ) : evidence.data.candidates.map((candidate) => (
                <article key={candidate.candidateId} className={`commercialEvidenceCandidate commercialEvidenceCandidate--${candidate.associationState}`}>
                  <div className="commercialEvidenceItemTop">
                    <span className="commercialEvidenceSourceTag">{candidate.source}</span>
                    <span>{candidate.associationState === 'suggested' ? 'Sugerida' : 'Ambigua'}</span>
                  </div>
                  <strong>{candidate.title}</strong>
                  <p>{candidate.reason}</p>
                  <p>{candidate.date ? formatDate(candidate.date) : 'Fecha no disponible'}{candidate.direction ? ` · ${candidate.direction}` : ''}{candidate.conversationId ? ' · conversación' : ''}{candidate.timing === 'upcoming' ? ' · próxima reunión' : candidate.timing === 'past' ? ' · reunión pasada' : ''}</p>
                  <div className="prototypeContextCard__actions">
                    <Button size="sm" disabled={evidence.decidingId === candidate.candidateId} onClick={() => void decideEvidence(candidate.candidateId, 'confirm')}>Confirmar vínculo</Button>
                    <Button size="sm" variant="secondary" disabled={evidence.decidingId === candidate.candidateId} onClick={() => void decideEvidence(candidate.candidateId, 'discard')}>Descartar</Button>
                    {candidate.url && <a className="prototypeMicrosoftLink" href={candidate.url} target="_blank" rel="noreferrer">Revisar fuente</a>}
                  </div>
                </article>
              ))}
              <details className="commercialEvidenceSecondary">
                <summary>Contactos, To Do, Teams y transcripciones</summary>
                <div className="commercialEvidenceSecondaryList">
                  {evidence.data.contacts.map((contact) => <p key={contact.personId}><strong>{contact.personName}</strong> · {contact.state === 'linked' ? 'Contacto Outlook vinculado' : contact.state === 'suggested' ? 'Coincidencia exacta por revisar' : 'Sin contacto Outlook exacto'}.</p>)}
                  {evidence.data.todo.map((item) => <p key={item.commitmentId}><strong>{item.title}</strong> · Sales: {item.salesState} · To Do: {item.mirror ? `${item.mirror.state}${item.mirror.divergence ? ' (divergencia)' : ''}` : 'sin espejo identificado'}.</p>)}
                  <p><strong>Teams</strong> · {sourceStateLabel(evidence.data.teams.state)}. {evidence.data.teams.note}</p>
                  <p><strong>Transcripciones</strong> · {sourceStateLabel(evidence.data.transcripts.state)} · {evidence.data.transcripts.count} metadatos encontrados{evidence.data.transcripts.meetings.length ? ` en ${evidence.data.transcripts.meetings.join(', ')}` : ''}. {evidence.data.transcripts.note}</p>
                </div>
              </details>
            </section>
          </div>
        </>
      )}

      {editing && (
        <div className="prototypeContextCard" style={{ marginBottom: 16 }}>
          <SectionTitle title="Editar relación" subtitle="Actualiza la prioridad explícita, el estado, disponibilidad, ownership y resumen de la relación." />
          <div className="prototypeRelationshipRow__grid">
            <label className="prototypeRelationshipRow__cell">
              <span className="prototypeMiniLabel">Estado de relación</span>
              <select value={estado} onChange={(e) => setEstado(e.target.value as RelationshipEstado)}>
                {ESTADOS.map((s) => <option key={s} value={s}>{estadoLabel[s]}</option>)}
              </select>
            </label>
            <label className="prototypeRelationshipRow__cell">
              <span className="prototypeMiniLabel">Prioridad comercial</span>
              <select value={prioridad} onChange={(e) => setPrioridad(e.target.value as RelationshipPriority | '')}>
                <option value="">Sin priorizar</option>
                {PRIORIDADES.map((value) => <option key={value} value={value}>{prioridadLabel[value]}</option>)}
              </select>
              <small>Se define manualmente y no cambia la clasificación ni el estado.</small>
            </label>
            <label className="prototypeRelationshipRow__cell">
              <span className="prototypeMiniLabel">Disponibilidad</span>
              <select value={activo ? 'activa' : 'desactivada'} onChange={(e) => setActivo(e.target.value === 'activa')}>
                <option value="activa">Activa en Sales</option>
                <option value="desactivada">Desactivada (conserva su historia)</option>
              </select>
            </label>
            {assignableUsers.length > 0 && <label className="prototypeRelationshipRow__cell"><span className="prototypeMiniLabel">Responsable de empresa</span><select value={responsableId} onChange={(event) => setResponsableId(event.target.value)}><option value="" disabled>Selecciona responsable…</option>{assignableUsers.map((user) => <option key={user.id} value={user.sharePointUserId ?? ''}>{user.email}</option>)}</select></label>}
            <label className="prototypeRelationshipRow__cell" style={{ gridColumn: '1 / -1' }}>
              <span className="prototypeMiniLabel">Resumen actual</span>
              <textarea value={resumen} onChange={(e) => setResumen(e.target.value)} rows={3} />
            </label>
          </div>
          {saveError && <p className="prototypeRelationshipRow__summary" role="alert">{saveError}</p>}
          <div className="prototypeContextCard__actions">
            <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar cambios'}</Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)} disabled={saving}>Cancelar</Button>
          </div>
        </div>
      )}

      <section className="prototypeSection">
        <SectionTitle title="Contexto de la relación" subtitle="Datos maestros de la organización." />
        <div className="prototypeRelationshipRow__grid">
          <Field label="Clasificación" value={o.clasificacion} />
          <Field label="Prioridad comercial" value={o.prioridadComercial ? prioridadLabel[o.prioridadComercial] : 'Sin priorizar'} />
          <Field label="Estado" value={estadoLabel[o.estadoRelacion]} />
          <Field label="Disponibilidad" value={o.activo ? 'Activa en Sales' : 'Desactivada'} />
          <Field label="País" value={o.pais} />
          <Field label="Segmento" value={o.segmento} />
          <Field label="Responsable" value={o.responsable} />
          <Field label="Dominios web" value={o.dominios.length ? o.dominios.join(', ') : null} />
        </div>
        {o.resumenActual && <p className="prototypeRelationshipRow__summary">{o.resumenActual}</p>}
      </section>

      <section className="prototypeSection">
        <SectionTitle
          title={`Personas (${detail.personas.length})`}
          subtitle="Contactos vinculados a esta relación."
          action={
            <Button
              size="sm"
              variant="secondary"
              onClick={() => addingPersona ? closePersonaForm() : openPersonaForm()}
            >
              {addingPersona ? 'Cancelar' : '+ Agregar persona'}
            </Button>
          }
        />

        {addingPersona && (
          <div ref={personaFormRef} className="prototypeContextCard" style={{ marginBottom: 16 }}>
            <p className="prototypeRelationshipRow__summary">
              Completa los datos manualmente. Nada se guardará hasta que presiones “Guardar persona”.
            </p>
            <label className="prototypeRelationshipRow__cell" style={{ marginBottom: 12 }}>
              <span className="prototypeMiniLabel">¿Ya existe esta persona? Busca por nombre o correo</span>
              <input
                type="text"
                value={personaSearch}
                onChange={(e) => setPersonaSearch(e.target.value)}
                placeholder="Escribe al menos 2 letras…"
              />
            </label>

            {searchResults.length > 0 && (
              <div className="prototypeRelationshipTray" style={{ marginBottom: 12 }}>
                {searchResults.map((p) => (
                  <article key={p.id} className="prototypeRelationshipRow">
                    <div className="prototypeRelationshipRow__titleBlock">
                      <span className="prototypeRelationshipRow__title">{p.title}</span>
                      <span className="prototypeRelationshipRow__responsible">
                        {[p.cargo, p.organizacionNombre ?? 'Sin organización'].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    <div className="prototypeContextCard__actions">
                      <Link to={`/personas/${p.id}`} className="prototypeMicrosoftLink">Ver ficha</Link>
                      <Button size="sm" variant="secondary" onClick={() => applyExistingPersona(p)}>
                        Usar estos datos
                      </Button>
                    </div>
                  </article>
                ))}
              </div>
            )}

            <div className="prototypeRelationshipRow__grid">
              <label className="prototypeRelationshipRow__cell">
                <span className="prototypeMiniLabel">Empresa seleccionada</span>
                <input type="text" value={o.title} readOnly />
              </label>
              <label className="prototypeRelationshipRow__cell">
                <span className="prototypeMiniLabel">Nombre *</span>
                <input
                  type="text"
                  value={personaForm.title}
                  onChange={(e) => setPersonaForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Nombre y apellido"
                />
              </label>
              <label className="prototypeRelationshipRow__cell">
                <span className="prototypeMiniLabel">Cargo</span>
                <input
                  type="text"
                  value={personaForm.cargo ?? ''}
                  onChange={(e) => setPersonaForm((f) => ({ ...f, cargo: e.target.value }))}
                  placeholder="Gerente de Operaciones"
                />
              </label>
              <label className="prototypeRelationshipRow__cell">
                <span className="prototypeMiniLabel">Correo</span>
                <input
                  type="email"
                  value={personaForm.email ?? ''}
                  onChange={(e) => setPersonaForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="nombre@empresa.com"
                />
                {emailConflict && (
                  <span className="prototypeRelationshipRow__summary" role="alert">
                    Ya registrado por <strong>{emailConflict.title}</strong>
                    {emailConflict.organizacionNombre ? ` (${emailConflict.organizacionNombre})` : ''} —{' '}
                    <Link to={`/personas/${emailConflict.id}`} className="prototypeLink">ver ficha</Link>
                  </span>
                )}
              </label>
              <label className="prototypeRelationshipRow__cell">
                <span className="prototypeMiniLabel">Teléfono</span>
                <input
                  type="tel"
                  value={personaForm.telefono ?? ''}
                  onChange={(e) => setPersonaForm((f) => ({ ...f, telefono: e.target.value }))}
                  placeholder="+56 9 1234 5678"
                />
              </label>
              <label className="prototypeRelationshipRow__cell">
                <span className="prototypeMiniLabel">Rol en la relación</span>
                <select
                  value={personaForm.rolEnRelacion ?? ''}
                  onChange={(e) =>
                    setPersonaForm((f) => ({
                      ...f,
                      rolEnRelacion: (e.target.value || null) as PersonaRolEnRelacion | null,
                    }))
                  }
                >
                  <option value="">—</option>
                  {ROLES_EN_RELACION.map((r) => (
                    <option key={r} value={r}>{rolEnRelacionLabel[r]}</option>
                  ))}
                </select>
              </label>
            </div>
            {personaError && <p className="prototypeRelationshipRow__summary" role="alert">{personaError}</p>}
            <div className="prototypeContextCard__actions">
              <Button size="sm" onClick={savePersona} disabled={personaSaving || Boolean(emailConflict)}>
                {personaSaving ? 'Guardando…' : 'Guardar persona'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={closePersonaForm}
                disabled={personaSaving}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {detail.personas.length === 0 ? (
          <p className="prototypeRelationshipRow__summary">Aún no hay personas vinculadas.</p>
        ) : (
          <div className="prototypeRelationshipTray">
            {detail.personas.map((p) => (
              <Link key={p.id} to={`/personas/${p.id}`} className="prototypeRelationshipRow commercialLinkedPerson">
                <div className="prototypeRelationshipRow__titleBlock">
                  <span className="prototypeRelationshipRow__title">{p.title}</span>
                  <span className="prototypeRelationshipRow__responsible">{[p.cargo, p.rolEnRelacion].filter(Boolean).join(' · ') || '—'}</span>
                </div>
                <span className="commercialLinkedPerson__action">Abrir ficha →</span>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section id="nueva-accion" ref={commitmentSectionRef} className="prototypeSection">
        <SectionTitle
          title="Próximo compromiso"
          subtitle="La siguiente acción abierta con esta relación."
          action={
            <Button
              size="sm"
              variant={addingCommitment ? 'secondary' : 'primary'}
              onClick={() => addingCommitment ? setAddingCommitment(false) : openCommitmentForm()}
            >
              {addingCommitment ? 'Cerrar formulario' : 'Agregar compromiso'}
            </Button>
          }
        />
        {addingCommitment && (
          <div className="prototypeContextCard" style={{ marginBottom: 16 }}>
            <CommitmentForm
              organizationId={o.id}
              organizationName={o.title}
              people={detail.personas.map((person) => ({ id: person.id, title: person.title }))}
              responsibles={assignableUsers.map((user) => ({ id: user.sharePointUserId ?? '', title: user.email })).filter((user) => Boolean(user.id))}
              onSave={saveCommitment}
              onCancel={() => setAddingCommitment(false)}
            />
          </div>
        )}
        {commitmentNotice && <p className="prototypeCommitmentForm__success" role="status">{commitmentNotice}</p>}
        {detail.nextAction ? (
          <div className="prototypeRelationshipRow">
            <div className="prototypeRelationshipRow__titleBlock">
              <span className="prototypeRelationshipRow__title">{detail.nextAction.title}</span>
              <span className="prototypeRelationshipRow__responsible">Vence: {formatDate(detail.nextAction.vencimiento)}</span>
            </div>
          </div>
        ) : (
          <p className="prototypeRelationshipRow__summary">Sin compromisos abiertos.</p>
        )}
        {detail.compromisosAbiertos.length > 0 && (
          <div className="prototypePeopleSearchList" style={{ marginTop: 12 }}>
            {detail.compromisosAbiertos.map((commitment) => {
              const updating = updatingCommitmentId === commitment.id
              return (
                <article key={commitment.id} className="prototypePeopleSearchRow">
                  <div className="prototypePeopleSearchRow__copy">
                    <strong>{commitment.title}</strong>
                    <span>
                      Vence: {formatDate(commitment.vencimiento)}
                      {commitment.responsable ? ` · Responsable: ${commitment.responsable}` : ''}
                    </span>
                    <span>{commitment.estado} · {synchronizationLabel(commitment.synchronization)}</span>
                    {commitment.descripcion && <span>{commitment.descripcion}</span>}
                  </div>
                  <div className="prototypeContextCard__actions">
                    <Button size="sm" variant="secondary" disabled={updating} onClick={() => void syncCommitment(commitment.id)}>Sincronizar con To Do</Button>
                    <Button size="sm" variant="ghost" disabled={updating} onClick={() => { if (window.confirm('¿Adoptar una tarea antigua identificada por este compromiso en tu propio To Do? No se asociará por título.')) void syncCommitment(commitment.id,true) }}>Adoptar tarea anterior</Button>
                    <Button size="sm" variant="secondary" disabled={updating} onClick={() => updateCommitmentAction(commitment.id, { estado: 'completado' }, 'Compromiso completado.')}>Completar</Button>
                    <Button size="sm" variant="secondary" disabled={updating} onClick={() => updateCommitmentAction(commitment.id, { estado: 'cancelado' }, 'Compromiso cancelado.')}>Cancelar</Button>
                    <Button size="sm" variant="ghost" disabled={updating} onClick={() => setReschedulingId(reschedulingId === commitment.id ? null : commitment.id)}>Reprogramar</Button>
                    {o.responsableId && commitment.responsableId !== o.responsableId && (
                      <Button size="sm" variant="ghost" disabled={updating} onClick={() => updateCommitmentAction(commitment.id, { responsableId: o.responsableId! }, `Compromiso reasignado a ${o.responsable ?? 'la persona responsable de la empresa'}.`)}>Asignar a owner</Button>
                    )}
                  </div>
                  {reschedulingId === commitment.id && (
                    <form className="prototypeContextCard__actions" onSubmit={(event) => {
                      event.preventDefault()
                      if (rescheduleDate) void updateCommitmentAction(commitment.id, { estado: 'reprogramado', vencimiento: rescheduleDate }, 'Compromiso reprogramado.')
                    }}>
                      <input aria-label="Nueva fecha" type="date" value={rescheduleDate} onChange={(event) => setRescheduleDate(event.target.value)} required />
                      <Button size="sm" type="submit" disabled={updating || !rescheduleDate}>Guardar nueva fecha</Button>
                    </form>
                  )}
                </article>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
